import React from 'react';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LabelChip from 'components/chip/LabelChip';
import { ArticleSummary, ArticleVisibility } from 'stores/ArticleStoreAdapter';

import ArticleCard from './ArticleCard';
import ArticleEmptyState from './ArticleEmptyState';
import ArticleListItem from './ArticleListItem';
import { GROUP_DISPLAY_LIMIT } from './articleUtils';

export type GroupMode = 'author' | 'label';

/** グループ内の表示レイアウト（カード / 一覧） */
export type ArticleLayout = 'card' | 'list';

type Group = {
  key: string;
  /** グループ見出し（Author は文字列、Label は色付き Chip） */
  labelNode: React.ReactNode;
  articles: ArticleSummary[];
};

type Props = {
  mode: GroupMode;
  /** カード / 一覧 の表示レイアウト（全記事モードから引き継ぐ） */
  layout: ArticleLayout;
  articles: ArticleSummary[];
  onOpen: (articleId: string) => void;
  onEdit?: (articleId: string) => void;
  onDelete?: (article: ArticleSummary) => void;
  onChangeVisibility?: (article: ArticleSummary, visibility: ArticleVisibility) => void;
};

/**
 * 取得済み記事（grouping=true の全件）を Author ごと / Labels ごとにグループ化して表示する。
 * 各グループは最新 GROUP_DISPLAY_LIMIT 件を初期表示し、超過分は「もっと見る」で展開（追加取得なし）。
 * layout に応じてカード（グリッド）/ 一覧（リスト行）を切り替える。
 */
const ArticleGroupList: React.FC<Props> = ({
  mode,
  layout,
  articles,
  onOpen,
  onEdit,
  onDelete,
  onChangeVisibility,
}) => {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

  const groups = React.useMemo<Group[]>(() => {
    const map = new Map<string, Group>();

    const push = (key: string, labelNode: React.ReactNode, article: ArticleSummary) => {
      const existing = map.get(key);
      if (existing) {
        existing.articles.push(article);
      } else {
        map.set(key, { key, labelNode, articles: [article] });
      }
    };

    articles.forEach((article) => {
      if (mode === 'author') {
        if (article.authors.length === 0) {
          push('__none__', '著者なし', article);
        } else {
          article.authors.forEach((a) => push(`author:${a.userName}`, a.userName, article));
        }
      } else {
        if (article.labels.length === 0) {
          push('__none__', 'ラベルなし', article);
        } else {
          article.labels.forEach((l) =>
            push(
              `label:${l.labelId}`,
              <LabelChip key={l.labelId} name={l.name} color={l.color} size="medium" />,
              article,
            ),
          );
        }
      }
    });

    // 各グループ内は公開日降順
    const result = Array.from(map.values());
    result.forEach((g) => g.articles.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
    return result;
  }, [articles, mode]);

  if (articles.length === 0) {
    return (
      <ArticleEmptyState
        icon={<ArticleOutlinedIcon />}
        title="表示できる記事がありません"
        description="公開された記事が増えると、ここに著者・ラベルごとにまとまって表示されます。"
      />
    );
  }

  return (
    <Stack spacing={0}>
      {groups.map((group, index) => {
        const isExpanded = expanded[group.key];
        const shown = isExpanded ? group.articles : group.articles.slice(0, GROUP_DISPLAY_LIMIT);
        const hasMore = group.articles.length > GROUP_DISPLAY_LIMIT;

        return (
          <Box
            key={group.key}
            sx={{
              // グループの境目を横線で区切る（先頭以外は上に区切り線＋余白）
              pt: index === 0 ? 0 : 3.5,
              mt: index === 0 ? 0 : 3.5,
              borderTop: index === 0 ? 0 : 1,
              borderColor: 'divider',
            }}
          >
            {/* グループ見出し（アクセントバーなし。グループの区切りは上の横線で表現） */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
              {typeof group.labelNode === 'string' ? (
                <Typography variant="subtitle1" fontWeight={700}>
                  {group.labelNode}
                </Typography>
              ) : (
                group.labelNode
              )}
              <Box
                sx={{
                  px: 1,
                  py: 0.1,
                  borderRadius: 5,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                }}
              >
                {group.articles.length}
              </Box>
            </Stack>

            {layout === 'card' ? (
              <Grid container spacing={2.5}>
                {shown.map((article) => (
                  <Grid key={`${group.key}-${article.articleId}`} size={{ xs: 12, md: 6 }}>
                    <ArticleCard
                      article={article}
                      onOpen={onOpen}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onChangeVisibility={onChangeVisibility}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack spacing={1}>
                {shown.map((article) => (
                  <ArticleListItem
                    key={`${group.key}-${article.articleId}`}
                    article={article}
                    onOpen={onOpen}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onChangeVisibility={onChangeVisibility}
                  />
                ))}
              </Stack>
            )}

            {hasMore && !isExpanded && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  size="small"
                  endIcon={<ExpandMoreIcon />}
                  onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: true }))}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 5,
                    px: 2,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                  }}
                >
                  もっと見る（残り {group.articles.length - GROUP_DISPLAY_LIMIT} 件）
                </Button>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ArticleGroupList;
